import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';
import { NotificationBell } from '../NotificationPanel';

// ─── Profile Edit Modal ───────────────────────────────────────────────────────
const ProfileEditModal = ({ user, onClose }) => {
  const [form, setForm] = useState({ full_name: '', email: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (!session) return;
        const { data } = await supabase?.from('user_profiles')?.select('full_name, email')?.eq('id', session?.user?.id)?.single();
        setForm(prev => ({
          ...prev,
          full_name: data?.full_name || session?.user?.email?.split('@')?.[0] || '',
          email: data?.email || session?.user?.email || '',
        }));
      } catch (err) {
        console.error('Load profile error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSave = async () => {
    if (form?.newPassword && form?.newPassword !== form?.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) throw new Error('No session');

      // Update user_profiles table
      await supabase?.from('user_profiles')?.update({ full_name: form?.full_name, email: form?.email, updated_at: new Date()?.toISOString() })?.eq('id', session?.user?.id);

      // Update password if provided
      if (form?.newPassword) {
        const { error: pwErr } = await supabase?.auth?.updateUser({ password: form?.newPassword });
        if (pwErr) throw pwErr;
      }

      setMessage({ type: 'success', text: 'Profile updated successfully.' });
      setTimeout(onClose, 1500);
    } catch (err) {
      setMessage({ type: 'error', text: err?.message || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[2000] flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg shadow-modal max-w-md w-full animate-fade-in">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <Icon name="User" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Profile Settings</h3>
              <p className="text-sm text-text-muted">Update your admin profile</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-hover transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {message && (
              <div className={`p-3 rounded-lg text-sm ${message?.type === 'success' ? 'bg-success-50 text-success' : 'bg-error-50 text-error'}`}>
                {message?.text}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Full Name</label>
              <input
                type="text"
                value={form?.full_name}
                onChange={e => setForm(prev => ({ ...prev, full_name: e?.target?.value }))}
                className="input-field w-full"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Email Address</label>
              <input
                type="email"
                value={form?.email}
                onChange={e => setForm(prev => ({ ...prev, email: e?.target?.value }))}
                className="input-field w-full"
                placeholder="your@email.com"
              />
            </div>
            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium text-text-secondary mb-3">Change Password (optional)</p>
              <div className="space-y-3">
                <input
                  type="password"
                  value={form?.newPassword}
                  onChange={e => setForm(prev => ({ ...prev, newPassword: e?.target?.value }))}
                  className="input-field w-full"
                  placeholder="New password"
                />
                <input
                  type="password"
                  value={form?.confirmPassword}
                  onChange={e => setForm(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                  className="input-field w-full"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
          </div>
        )}

        <div className="p-6 border-t border-border flex space-x-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-surface-hover transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex-1 px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Notifications Modal ──────────────────────────────────────────────────────
const NotificationsModal = ({ onClose }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const { data } = await supabase?.from('system_alerts')?.select('*')?.order('created_at', { ascending: false })?.limit(50);
        setAlerts(data || []);
      } catch (err) {
        console.error('Fetch alerts error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await supabase?.from('system_alerts')?.update({ status: 'acknowledged', acknowledged_at: new Date()?.toISOString() })?.in('status', ['active', 'pending']);
      setAlerts(prev => prev?.map(a => ({ ...a, status: 'acknowledged' })));
    } catch (err) {
      console.error('Mark all read error:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  const severityColor = (severity) => {
    if (severity === 'critical') return 'bg-error text-white';
    if (severity === 'high') return 'bg-warning text-white';
    if (severity === 'medium') return 'bg-secondary text-white';
    return 'bg-surface-hover text-text-secondary';
  };

  const statusColor = (status) => {
    if (status === 'resolved') return 'text-success';
    if (status === 'acknowledged') return 'text-secondary';
    return 'text-warning';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[2000] flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg shadow-modal max-w-2xl w-full max-h-[85vh] flex flex-col animate-fade-in">
        <div className="p-6 border-b border-border flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning-100 rounded-full flex items-center justify-center">
              <Icon name="Bell" size={20} color="var(--color-warning)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">All Notifications</h3>
              <p className="text-sm text-text-muted">{alerts?.length} system alerts</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleMarkAllRead}
              disabled={markingAll}
              className="text-sm text-secondary hover:text-secondary-700 font-medium disabled:opacity-50 px-3 py-1 border border-border rounded-lg hover:bg-surface-hover transition-colors"
            >
              {markingAll ? 'Marking...' : 'Mark All Read'}
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-hover transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 flex justify-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : alerts?.length === 0 ? (
            <div className="p-8 text-center text-text-muted">
              <Icon name="BellOff" size={32} className="mx-auto mb-2 opacity-40" />
              <p>No notifications found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {alerts?.map(alert => (
                <div key={alert?.id} className="p-4 hover:bg-surface-hover transition-colors">
                  <div className="flex items-start justify-between space-x-3">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${severityColor(alert?.severity)}`}>
                        {alert?.severity || 'info'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{alert?.title || alert?.message}</p>
                        {alert?.message && alert?.title && (
                          <p className="text-xs text-text-muted mt-0.5 line-clamp-2">{alert?.message}</p>
                        )}
                        <p className="text-xs text-text-muted mt-1">
                          {alert?.created_at ? new Date(alert.created_at)?.toLocaleString() : ''}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium flex-shrink-0 ${statusColor(alert?.status)}`}>
                      {alert?.status || 'active'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-surface-hover transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── NavigationHeader ─────────────────────────────────────────────────────────
const NavigationHeader = ({ onMenuToggle, user = null, onSignOut = null, signingOut = false }) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [liveAlerts, setLiveAlerts] = useState([]);

  // Load live notification count from system_alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const { data } = await supabase?.from('system_alerts')?.select('id, title, message, severity, created_at')?.in('status', ['active', 'pending'])?.order('created_at', { ascending: false })?.limit(5);
        setLiveAlerts(data || []);
      } catch (err) {
        // silently fail — fallback to empty
      }
    };
    fetchAlerts();
  }, []);

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    if (onSignOut) onSignOut();
  };

  const handleProfileSettings = () => {
    setShowUserMenu(false);
    setShowProfileModal(true);
  };

  const handleHelpSupport = () => {
    setShowUserMenu(false);
    navigate('/dispute-support-screen');
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-surface border-b border-border z-200 h-16">
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          {/* Left Section */}
          <div className="flex items-center space-x-4">
            <button
              onClick={onMenuToggle}
              className="lg:hidden p-2 rounded-lg hover:bg-surface-hover transition-colors duration-200"
              aria-label="Toggle menu"
            >
              <Icon name="Menu" size={24} color="currentColor" />
            </button>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-accent" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <span className="text-xl font-bold text-primary hidden sm:block">OnTheGo</span>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2">
            {/* System Status */}
            <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-success-50 rounded-full">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-sm text-success font-medium">System Online</span>
            </div>

            {/* Notification Bell */}
            <div className="relative">
              <NotificationBell
                iconColor="var(--color-text-primary)"
                badgeColor="bg-error"
              />
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={handleUserMenuToggle}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-surface-hover transition-colors duration-200"
                aria-label="User menu"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <span className="hidden md:block text-sm font-medium text-text-primary">
                  {user?.name || 'Admin User'}
                </span>
                <Icon name="ChevronDown" size={16} color="currentColor" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-surface rounded-lg shadow-modal border border-border z-[1000]">
                  <div className="p-4 border-b border-border">
                    <p className="font-medium text-text-primary">{user?.name || 'Admin User'}</p>
                    <p className="text-sm text-text-muted">{user?.email || 'admin@onthego.com'}</p>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={handleProfileSettings}
                      className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Icon name="User" size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={handleProfileSettings}
                      className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Account Settings</span>
                    </button>
                    <button
                      onClick={handleHelpSupport}
                      className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Icon name="HelpCircle" size={16} />
                      <span>Help & Support</span>
                    </button>
                  </div>
                  <div className="border-t border-border py-2">
                    <button
                      onClick={handleLogout}
                      disabled={signingOut}
                      className="w-full text-left px-4 py-2 text-sm text-error hover:bg-error-50 transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>{signingOut ? 'Signing out...' : 'Sign Out'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {/* Profile/Account Settings Modal */}
      {showProfileModal && (
        <ProfileEditModal user={user} onClose={() => setShowProfileModal(false)} />
      )}
      {/* Full Notifications Modal */}
      {showNotificationsModal && (
        <NotificationsModal onClose={() => setShowNotificationsModal(false)} />
      )}
    </>
  );
};

export default NavigationHeader;